import type { EmployeeView } from '../../../services/employees/employees.service';
import type { ProductView, UpdateProductRequest } from '../../../services/products/products.service';
import type { AreaTableView, AreaView } from '../../../services/areas/areas.service';
import type { AreaPrintRule } from 'shared-models/browser';
import { roundMoney } from 'shared-models/browser';
import { addFormRow, removeFormRow, updateFormRowField } from '../../../utils/form-row-list';
import type {
  AreaEditBaseline,
  EmployeeEditBaseline,
  EmployeeFormState,
  ProductEditBaseline,
  ProductFormRow,
  ProductFormState,
  TableFormState,
} from './pos-shell-page.types';
import { createProductFormRow } from './pos-shell-page.helpers';

export const buildEmployeeEditForm = (employee: EmployeeView): EmployeeFormState => ({
  fullName: employee.fullName,
  pin: '',
  role: employee.role,
  baseHourlySalary: employee.baseHourlySalary == null ? '' : String(employee.baseHourlySalary),
});

export const buildEmployeeBaseline = (employee: EmployeeView): EmployeeEditBaseline => ({
  fullName: employee.fullName,
  role: employee.role,
  baseHourlySalary: employee.baseHourlySalary ?? null,
});

export const buildProductEditForm = (product: ProductView): ProductFormState => ({
  name: product.name,
  price: String(product.price),
  printerIds: product.printerIds,
  kds: product.kds,
  categoryId: product.categoryId,
  areaPrintEnabled: false,
  areaPrintRuleId: null,
});

export const buildProductBaseline = (product: ProductView): ProductEditBaseline => ({
  name: product.name,
  price: product.price,
  printerIds: product.printerIds,
  kds: product.kds,
  categoryId: product.categoryId,
});

export const buildProductUpdatePayload = (
  next: { name: string; price: number; printerIds: number[]; kds: boolean; categoryId: number | null },
  baseline: ProductEditBaseline | null,
): UpdateProductRequest => {
  const updatePayload: UpdateProductRequest = {};

  if (baseline === null || next.name !== baseline.name) {
    updatePayload.name = next.name;
  }

  if (baseline === null || next.price !== baseline.price) {
    updatePayload.price = next.price;
  }

  const baselinePrinterKey = baseline ? [...baseline.printerIds].sort().join('|') : '';
  const nextPrinterKey = [...next.printerIds].sort().join('|');
  if (baseline === null || nextPrinterKey !== baselinePrinterKey) {
    updatePayload.printerIds = next.printerIds;
  }

  if (baseline === null || next.kds !== baseline.kds) {
    updatePayload.kds = next.kds;
  }

  if (baseline === null || next.categoryId !== baseline.categoryId) {
    updatePayload.categoryId = next.categoryId;
  }

  return updatePayload;
};

export const buildAreaEditForm = (area: AreaView): { name: string } => ({
  name: area.name,
});

export const buildAreaBaseline = (area: AreaView): AreaEditBaseline => ({
  name: area.name,
});

export const buildTableEditForm = (table: AreaTableView): TableFormState => ({
  number: table.number,
  shape: table.shape,
});

// Scans a simple row-major grid of slots (each slot = w×h with a fixed gap) and
// returns the first position that does not overlap any existing table.
export const findFreeTablePosition = (
  existingTables: AreaTableView[],
  w: number,
  h: number,
): { x: number; y: number } => {
  const GAP = 16;
  const START = 24;
  const MAX_COLS = 15;
  const MAX_ROWS = 30;

  for (let row = 0; row < MAX_ROWS; row++) {
    for (let col = 0; col < MAX_COLS; col++) {
      const x = START + col * (w + GAP);
      const y = START + row * (h + GAP);
      const overlaps = existingTables.some(
        (t) =>
          x < t.positionX + t.width &&
          x + w > t.positionX &&
          y < t.positionY + t.height &&
          y + h > t.positionY,
      );
      if (!overlaps) {
        return { x, y };
      }
    }
  }
  // Fallback (grid full): stack below the lowest occupied edge, not below a count of
  // tables — using the count can land on top of an existing table in irregular layouts.
  const maxBottom = existingTables.reduce(
    (lowest, t) => Math.max(lowest, t.positionY + t.height),
    0,
  );
  return { x: START, y: maxBottom + GAP };
};

// For clipboard paste: try to keep new tables near the copied table's row/area,
// then fall back to the global row-major scanner.
export const findFreeTablePositionNear = (
  existingTables: AreaTableView[],
  w: number,
  h: number,
  preferredX: number,
  preferredY: number,
): { x: number; y: number } => {
  const GAP = 16;
  const STEP_X = w + GAP;
  const STEP_Y = h + GAP;
  const MAX_ROW_SWEEP = 20;
  const MAX_COL_SWEEP = 20;

  const hasOverlap = (x: number, y: number): boolean =>
    existingTables.some(
      (t) =>
        x < t.positionX + t.width &&
        x + w > t.positionX &&
        y < t.positionY + t.height &&
        y + h > t.positionY,
    );

  const tryPosition = (x: number, y: number): { x: number; y: number } | null => {
    const normalizedX = Math.max(0, Math.round(x));
    const normalizedY = Math.max(0, Math.round(y));
    return hasOverlap(normalizedX, normalizedY) ? null : { x: normalizedX, y: normalizedY };
  };

  const baseX = Math.max(0, Math.round(preferredX));
  const baseY = Math.max(0, Math.round(preferredY));

  // 1) Keep the same row first: expand right then left from the copied table.
  for (let offset = 1; offset <= MAX_COL_SWEEP; offset += 1) {
    const right = tryPosition(baseX + offset * STEP_X, baseY);
    if (right) {
      return right;
    }
    const left = tryPosition(baseX - offset * STEP_X, baseY);
    if (left) {
      return left;
    }
  }

  // 2) Expand by nearby rows while keeping columns near the copied table.
  for (let rowOffset = 1; rowOffset <= MAX_ROW_SWEEP; rowOffset += 1) {
    const yDown = baseY + rowOffset * STEP_Y;
    const yUp = baseY - rowOffset * STEP_Y;
    for (let colOffset = 0; colOffset <= MAX_COL_SWEEP; colOffset += 1) {
      const rightX = baseX + colOffset * STEP_X;
      const leftX = baseX - colOffset * STEP_X;

      const downRight = tryPosition(rightX, yDown);
      if (downRight) {
        return downRight;
      }
      const downLeft = tryPosition(leftX, yDown);
      if (downLeft) {
        return downLeft;
      }
      const upRight = tryPosition(rightX, yUp);
      if (upRight) {
        return upRight;
      }
      const upLeft = tryPosition(leftX, yUp);
      if (upLeft) {
        return upLeft;
      }
    }
  }

  return findFreeTablePosition(existingTables, w, h);
};

// Lowest free numeric table name among the given active tables (fills gaps, else max+1).
export const nextAvailableTableNumber = (tables: Pick<AreaTableView, 'number' | 'isActive'>[]): string => {
  const activeNumbers = new Set(
    tables.filter((table) => table.isActive).map((table) => Number.parseInt(table.number, 10)),
  );
  const highest = tables
    .filter((table) => table.isActive)
    .reduce((max, table) => Math.max(max, Number.parseInt(table.number, 10) || 0), 0);

  for (let candidate = 1; candidate <= highest; candidate += 1) {
    if (!activeNumbers.has(candidate)) {
      return String(candidate);
    }
  }
  return String(highest + 1);
};

// Allocates `count` table numbers from the global free numeric space, always from
// the lowest available number upward (fills gaps first, then grows past max).
export const nextAvailableTableNumbers = (
  count: number,
  tables: Pick<AreaTableView, 'number' | 'isActive'>[],
): string[] => {
  if (count <= 0) {
    return [];
  }
  const activeNumbers = new Set(
    tables.filter((table) => table.isActive).map((table) => Number.parseInt(table.number, 10)),
  );
  const allocated: string[] = [];
  let candidate = 1;
  while (allocated.length < count) {
    if (!activeNumbers.has(candidate)) {
      allocated.push(String(candidate));
      activeNumbers.add(candidate);
    }
    candidate += 1;
  }
  return allocated;
};

// For copy/paste: prefer sourceNumber+1, else fall back to nextAvailableTableNumber.
export const nextTableNumberAfter = (
  sourceNumber: string,
  tables: Pick<AreaTableView, 'number' | 'isActive'>[],
): string => {
  const parsed = Number.parseInt(sourceNumber, 10);
  if (!Number.isFinite(parsed)) {
    return nextAvailableTableNumber(tables);
  }
  const candidate = String(parsed + 1);
  const taken = tables.some((table) => table.isActive && table.number === candidate);
  return taken ? nextAvailableTableNumber(tables) : candidate;
};

// Finds a disabled table matching the target number, for reactivation instead of a new row.
export const findReusableDisabledTable = (
  tables: AreaTableView[],
  targetNumber: string,
): AreaTableView | null =>
  tables.find((table) => !table.isActive && table.number === targetNumber) ?? null;

export const addProductFormRow = (rows: ProductFormRow[]): ProductFormRow[] =>
  addFormRow(rows, createProductFormRow());

export const removeProductFormRow = (rows: ProductFormRow[], index: number): ProductFormRow[] =>
  removeFormRow(rows, index);

export const updateProductFormRowField = <K extends keyof ProductFormState>(
  rows: ProductFormRow[],
  index: number,
  field: K,
  value: ProductFormState[K],
): ProductFormRow[] =>
  // Cast is safe: ProductFormRow extends ProductFormState, TS just can't prove it here.
  updateFormRowField<ProductFormRow, K>(rows, index, field, value as ProductFormRow[K]);

export const updateProductFormRowImage = (
  rows: ProductFormRow[],
  index: number,
  imageFile: File | null,
): ProductFormRow[] => updateFormRowField<ProductFormRow, 'imageFile'>(rows, index, 'imageFile', imageFile);

export type ProductFormRowValidationError = {
  rowIndex: number;
  message: string;
};

export const validateProductFormRows = (rows: ProductFormRow[]): ProductFormRowValidationError | null => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const normalizedName = row.name.trim();
    if (!normalizedName) {
      return { rowIndex, message: `מוצר ${rowIndex + 1}: שם המוצר נדרש` };
    }
    const parsedPrice = Number(row.price);
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      return { rowIndex, message: `מוצר ${rowIndex + 1}: מחיר לא תקין` };
    }
    if (row.areaPrintEnabled && !row.areaPrintRuleId) {
      return { rowIndex, message: `מוצר ${rowIndex + 1}: יש לבחור הגדרת הדפסה אזורית` };
    }
  }
  return null;
};

export const findAreaPrintRuleIdForProduct = (
  rules: AreaPrintRule[],
  productId: number,
): string | null => {
  const owner = rules.find((rule) => rule.productIds.includes(productId));
  return owner?.id ?? null;
};

export const applyAreaPrintRuleToProduct = (
  rules: AreaPrintRule[],
  productId: number,
  targetRuleId: string | null,
): { rules: AreaPrintRule[]; changed: boolean } => {
  let changed = false;
  const nextRules = rules.map((rule) => {
    const hasProduct = rule.productIds.includes(productId);
    if (rule.id === targetRuleId) {
      if (hasProduct) {
        return rule;
      }
      changed = true;
      return { ...rule, productIds: [...rule.productIds, productId] };
    }
    if (!hasProduct) {
      return rule;
    }
    changed = true;
    return { ...rule, productIds: rule.productIds.filter((id) => id !== productId) };
  });
  return { rules: nextRules, changed };
};

export type ProductCreatePayload = {
  name: string;
  price: number;
  printerIds: number[];
  kds: boolean;
  categoryId: number | null;
};

export const buildProductCreatePayload = (row: ProductFormRow): ProductCreatePayload => ({
  name: row.name.trim(),
  price: roundMoney(Number(row.price)),
  printerIds: row.printerIds,
  kds: row.kds,
  categoryId: row.categoryId,
});